import React, { useState, useMemo, useRef } from 'react';
import { useCollapse } from '../../hooks';
import { Typography, Tooltip, Button, Dropdown } from 'antd';
import classNames from 'classnames';
import { InfoCircleOutlined, MoreOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import { SwitchSummaryViewMode, ViewModeToggle } from '../../components';
import { LoaderAnimation } from '../LoaderAnimation';
import { ConfirmModal } from '../../components';
import { SUMMARY_SECTION_TOOLTIP } from '../../constants/discovery';
import { ViewModeState } from '../../constants';
import WOWO from '../../lib/wowo';
import { exportSummaryAsCsv, exportSummaryAsPdf } from './DiscoverySummaryExportHelpers';
import { useInventorySummaryData } from './useInventorySummaryData';
import { InventoryCardsSection } from './InventoryCardsSection';
import { SummaryDetailsSection } from './SummaryDetailsSection';
import { SummaryEmptyState } from './SummaryEmptyState';

import './SummaryWrapper.scss';

const { Text, Title, Paragraph } = Typography;

export const SummaryWrapper = ({
    title,
    className = '',
    onDownClick,
    setDashboardHasNoData = () => {},
    canRenderImportButton = false,
    isWaitingForUpload = false,
    showCardEllipsis = true,
    showMarkReadyInfo = true,
    showViewModeToggle = true,
    collapsible = false,
    onFilterClick,
    ...props
}) => {
    const summaryWrapperRef = useRef(null);
    const [viewMode, setViewMode] = useState(ViewModeState.OPEN);
    const [collapsed, toggle] = useCollapse(false);
    const [showIgnoreOptimizationWarningModal, setShowIgnoreOptimizationWarningModal] = useState(false);
    const [optimizationAction, setOptimizationAction] = useState(null);

    const {
        summaryData,
        isLoading,
        isDataReady,
        inventoryTotals,
        hasNoData,
        handleIgnoreOptimizations: runIgnoreOptimizations,
    } = useInventorySummaryData({ setDashboardHasNoData, canRenderImportButton });

    const loadingOrNoData = isLoading || !isDataReady;
    const isExpanded = viewMode === ViewModeState.OPEN;

    const handleConfirmModal = (optimization) => {
        setShowIgnoreOptimizationWarningModal(true);
        setOptimizationAction(optimization);
    };

    const handleIgnoreConfirmed = async () => {
        const success = await runIgnoreOptimizations(optimizationAction);
        if (success) {
            setOptimizationAction(null);
            setShowIgnoreOptimizationWarningModal(false);
        }
    };

    const exportDropdownItems = useMemo(
        () => [
            { key: 'csv', label: 'Export as CSV', onClick: () => exportSummaryAsCsv(summaryData) },
            { key: 'pdf', label: 'Export as PDF', onClick: () => exportSummaryAsPdf(summaryWrapperRef.current) },
        ],
        [summaryData],
    );

    const handleViewModeChange = (value) => {
        if (loadingOrNoData || hasNoData || isWaitingForUpload) return;
        setViewMode(value);
        if (onDownClick) onDownClick(value);
    };

    const isViewModeDisabled = loadingOrNoData || hasNoData || isWaitingForUpload;

    return (
        <>
            {hasNoData ? (
                <SummaryEmptyState />
            ) : (
                <div
                    ref={summaryWrapperRef}
                    className={classNames('panel-wrapper', 'summary-wrapper', className, {
                        'view-mode-collapsed': viewMode === ViewModeState.COLLAPSED,
                        'view-mode-hidden': viewMode === ViewModeState.HIDDEN,
                        'is-collapsed': collapsed,
                    })}
                    {...props}
                >
                    <div
                        className={classNames('summary-wrapper-header', {
                            'summary-wrapper-header--clickable': collapsible,
                        })}
                        onClick={collapsible ? toggle : undefined}
                        aria-expanded={collapsible ? !collapsed : undefined}
                    >
                        <div className="summary-title-and-tooltip">
                            <Title level={2} className="semi-bold summary-wrapper-title">
                                {title}
                            </Title>
                            <Tooltip
                                className="summary-tooltip"
                                classNames={{ root: 'summary-tooltip-content' }}
                                placement="right"
                                title={SUMMARY_SECTION_TOOLTIP}
                            >
                                <InfoCircleOutlined className="summary-tooltip-icon" />
                            </Tooltip>
                        </div>
                        <div className="summary-wrapper-header-right">
                            {showViewModeToggle && (
                                <div className="summary-wrapper-controls" onClick={(e) => e.stopPropagation()}>
                                    {WOWO.isEnabled('new_summary_cards') ? (
                                        <SwitchSummaryViewMode
                                            value={viewMode}
                                            onChange={handleViewModeChange}
                                            disabled={isViewModeDisabled}
                                        />
                                    ) : (
                                        <ViewModeToggle
                                            value={viewMode}
                                            onChange={handleViewModeChange}
                                            disabled={isViewModeDisabled}
                                        />
                                    )}
                                    <Dropdown
                                        trigger={['click']}
                                        placement="bottomRight"
                                        menu={{ items: exportDropdownItems }}
                                        disabled={loadingOrNoData}
                                    >
                                        <Button type="text" icon={<MoreOutlined />} />
                                    </Dropdown>
                                </div>
                            )}
                            {collapsible &&
                                (collapsed ? (
                                    <RightOutlined className="summary-section-caret" />
                                ) : (
                                    <DownOutlined className="summary-section-caret" />
                                ))}
                        </div>
                    </div>
                    <div className={classNames('summary-wrapper-body', { collapsed })}>
                        <div className="summary-wrapper-content">
                            <div className="inventory-cards-grid">
                                {loadingOrNoData ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <div key={`skeleton-${i}`} className="inventory-card-skeleton" />
                                    ))
                                ) : (
                                    <InventoryCardsSection
                                        summaryData={summaryData}
                                        inventoryTotals={inventoryTotals}
                                        isExpanded={isExpanded}
                                        showCardEllipsis={showCardEllipsis}
                                        onIgnoreOptimizations={handleConfirmModal}
                                        onFilterClick={onFilterClick}
                                    />
                                )}
                            </div>
                            {isExpanded && (
                                <div className="summary-details-grid">
                                    {loadingOrNoData ? (
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <div
                                                key={`skeleton-detail-${i}`}
                                                className="summary-detail-card-skeleton"
                                            />
                                        ))
                                    ) : (
                                        <SummaryDetailsSection
                                            summaryData={summaryData}
                                            inventoryTotals={inventoryTotals}
                                            showCardEllipsis={showCardEllipsis}
                                            onIgnoreOptimizations={handleConfirmModal}
                                        />
                                    )}
                                </div>
                            )}
                            {!isWaitingForUpload && loadingOrNoData && (
                                <div className="summary-wrapper-loading-overlay">
                                    <div className="summary-wrapper-loading-animation">
                                        <LoaderAnimation size="small" color="whiteBackground" />
                                    </div>
                                    <Text className="summary-wrapper-loading-text">Loading Inventory Summary...</Text>
                                </div>
                            )}
                            {!loadingOrNoData && viewMode !== ViewModeState.HIDDEN && showMarkReadyInfo && (
                                <Text className="small text-light">
                                    Machines marked as &ldquo;Ready&rdquo; are excluded from over- and under-provisioned
                                    metrics.
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal
                title="Notice"
                open={showIgnoreOptimizationWarningModal}
                onCancel={() => setShowIgnoreOptimizationWarningModal(false)}
                onContinue={handleIgnoreConfirmed}
            >
                <Paragraph>This action will ignore the current optimization type.</Paragraph>
                <Paragraph>
                    Some machines may still have other recommended optimizations that must be addressed before they can
                    be marked as ready for migration.
                </Paragraph>
            </ConfirmModal>
        </>
    );
};
